// Page de détail Artisan - VERSION LICORNE ULTIME
// IA Premium + Bouton flottant intelligent + Comportement démo crédible
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, MapPin, Star, Mail, Clock, CheckCircle, MessageSquare,
  FileText, ChevronRight, Building2, Wrench, Euro, Send, User, Calendar,
  Bot, Zap, TrendingUp, Heart, Share2, Shield, Award, Eye, Users,
  Camera, Play, BookOpen, Sparkles, ThumbsUp, MessageCircle, ExternalLink,
  X, Loader2, ChevronDown, Quote, Image as ImageIcon, Download, FileCheck,
  Home, Newspaper, Film, Phone
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { useDemoMode } from '../../../components/demo/DemoModeProvider';

import { API_URL } from '../../../config/api';
import { MOCK_ARTISANS } from './ArtisansPage';
import { useBoutiqueCheckout } from '../../../hooks/useBoutiqueCheckout';

// Mock data pour démonstration
const MOCK_REVIEWS = [
  { id: 1, author: 'Marie D.', rating: 5, date: '2026-03-15', text: 'Travail impeccable, très professionnel. Je recommande vivement !', avatar: 'https://randomuser.me/api/portraits/women/44.jpg' },
  { id: 2, author: 'Pierre L.', rating: 5, date: '2026-03-10', text: 'Rapide, efficace et à l\'écoute. Prix correct pour la qualité du travail.', avatar: 'https://randomuser.me/api/portraits/men/32.jpg' },
  { id: 3, author: 'Sophie M.', rating: 4, date: '2026-02-28', text: 'Bon artisan, ponctuel et soigneux. Petit retard sur la fin du chantier mais résultat top.', avatar: 'https://randomuser.me/api/portraits/women/68.jpg' },
];

const MOCK_GALLERY = [
  { id: 1, type: 'before_after', before: 'https://images.unsplash.com/photo-1585128792020-803d29415281?w=400', after: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400', title: 'Rénovation salle de bain' },
  { id: 2, type: 'project', image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400', title: 'Installation cuisine' },
  { id: 3, type: 'project', image: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=400', title: 'Pose carrelage terrasse' },
  { id: 4, type: 'project', image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400', title: 'Réfection murs' },
];

const MOCK_POSTS = [
  { id: 1, content: 'Nouveau chantier terminé ! Rénovation complète d\'une salle de bain à Lyon. Merci à mes clients pour leur confiance.', image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400', likes: 24, comments: 5, date: '2026-03-28' },
  { id: 2, content: 'Conseil du jour : pour éviter les fuites, faites vérifier vos joints tous les 5 ans !', likes: 45, comments: 12, date: '2026-03-25' },
];

const MOCK_ARTICLES = [
  { id: 1, title: 'Comment choisir son artisan plombier ?', excerpt: 'Les 5 critères essentiels pour ne pas se tromper...', readTime: '3 min', date: '2026-03-20' },
  { id: 2, title: '10 astuces pour économiser l\'eau chez soi', excerpt: 'Des gestes simples qui font la différence sur votre facture...', readTime: '5 min', date: '2026-03-15' },
];

// ═══════════════════════════════════════════════════════════════
// COMPOSANT CHAT IA - VERSION DÉMO INTELLIGENTE
// ═══════════════════════════════════════════════════════════════
function ArtisanAIChat({ shop, isOpen, onClose }) {
  const shopName = shop?.shop_name || 'cet artisan';
  
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: `Bonjour ! Je suis l'assistant IA de **${shopName}**.\n\nJe suis paramétré pour aider les clients de cette entreprise, répondre aux questions sur ses services, son activité et orienter les demandes.\n\nComme il s'agit actuellement d'une **version démo**, je ne peux pas encore traiter un vrai dossier client ou générer un devis réel pour cette entreprise.\n\nEn revanche, je peux répondre à tes questions sur le fonctionnement d'**AbracadaBati** et du projet AbracadaWorld !`
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const { requireAuth, isAuthenticated } = useDemoMode();
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Réponses IA intelligentes en mode démo
  const generateAIResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();
    
    // Questions sur le devis / prix réel
    if (msg.includes('devis') || msg.includes('prix') || msg.includes('combien') || msg.includes('coût') || msg.includes('tarif')) {
      return `Je suis ici dans une **version démonstration**, donc je ne peux pas encore traiter un vrai dossier client ou générer un devis réel pour ${shopName}.\n\nEn version finale, l'assistant IA pourra :\n• Qualifier ta demande\n• Estimer un budget indicatif\n• Préparer une demande de devis personnalisée\n• Vérifier les disponibilités\n\nTu souhaites en savoir plus sur AbracadaBati ?`;
    }
    
    // Questions sur la disponibilité
    if (msg.includes('disponible') || msg.includes('disponibilité') || msg.includes('rdv') || msg.includes('rendez-vous')) {
      return `En mode démo, je n'ai pas accès au calendrier réel de ${shopName}.\n\nMais en version finale, je pourrai :\n• Consulter les créneaux disponibles\n• Proposer des dates\n• Réserver directement un rendez-vous\n\nC'est l'un des super-pouvoirs d'AbracadaBati ! Chaque artisan aura son propre assistant IA.`;
    }
    
    // Questions sur AbracadaBati
    if (msg.includes('abracadabati') || msg.includes('abracadaworld') || msg.includes('plateforme') || msg.includes('comment ça marche')) {
      return `**AbracadaBati** est le premier centre commercial virtuel du bâtiment !\n\n🏗️ **Pour les particuliers :**\n• Trouve des artisans vérifiés\n• Compare les services\n• Obtiens des devis gratuits\n• Bénéficie d'un assistant IA 24/7\n\n👷 **Pour les pros :**\n• Vitrine professionnelle gratuite\n• CRM intégré\n• Assistant IA dédié\n• **Zéro commission** sur les ventes\n\n🚀 **Le Programme Fondateur :**\nRejoins les premiers artisans et bénéficie d'avantages exclusifs à vie !`;
    }
    
    // Questions sur le programme fondateur
    if (msg.includes('fondateur') || msg.includes('programme') || msg.includes('offre') || msg.includes('gratuit')) {
      return `Le **Programme Fondateur** est une offre exclusive pour les premiers artisans !\n\n✨ **Avantages à vie :**\n• Boutique professionnelle gratuite\n• Assistant IA personnalisé\n• Badge "Fondateur" distinctif\n• Accès prioritaire aux nouvelles fonctionnalités\n• Zéro commission, zéro abonnement\n\nC'est le moment idéal pour rejoindre AbracadaBati avant l'ouverture officielle le 1er Juillet 2026 !`;
    }
    
    // Questions sur l'IA
    if (msg.includes('ia') || msg.includes('assistant') || msg.includes('intelligence') || msg.includes('robot')) {
      return `L'**Assistant IA** est un super-pouvoir unique d'AbracadaBati !\n\nChaque artisan dispose de son propre assistant, paramétré avec :\n• Ses services\n• Ses tarifs\n• Ses disponibilités\n• Son expertise métier\n\n**Ce que l'IA peut faire :**\n• Répondre aux questions 24/7\n• Qualifier les demandes\n• Orienter vers les bons services\n• Préparer des estimations\n• Assister les prospects\n\nC'est comme avoir un commercial qui ne dort jamais !`;
    }
    
    // Questions sur les services de l'artisan
    if (msg.includes('service') || msg.includes('fait') || msg.includes('propose') || msg.includes('spécialité')) {
      const services = shop?.services?.slice(0, 4).map(s => typeof s === 'string' ? s : s.name).join(', ') || 'divers services';
      return `${shopName} propose notamment : ${services}.\n\nEn version finale, je pourrai te donner plus de détails sur chaque service, les tarifs indicatifs et t'aider à formuler une demande de devis précise.\n\nTu as une question sur un service en particulier ?`;
    }
    
    // Réponse par défaut intelligente
    return `Merci pour ta question ! En tant qu'assistant IA en version démo, je suis limité dans mes réponses spécifiques à ${shopName}.\n\nMais je peux t'aider avec :\n• Le fonctionnement d'AbracadaBati\n• Les avantages pour particuliers et pros\n• Le Programme Fondateur\n• Comment fonctionne l'Assistant IA\n\nQue souhaites-tu savoir ?`;
  };
  
  const handleSend = async () => {
    if (!input.trim()) return;
    
    if (!isAuthenticated) {
      requireAuth(null, 'discuter avec l\'assistant IA');
      return;
    }
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTyping(true);
    
    // Simuler un délai de réflexion
    setTimeout(() => {
      const response = generateAIResponse(userMessage);
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      setIsTyping(false);
    }, 1200);
  };
  
  // Formatage du texte avec **bold**
  const formatMessage = (text) => {
    return text.split('\n').map((line, i) => (
      <span key={i}>
        {line.split(/(\*\*[^*]+\*\*)/).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j} className="font-semibold">{part.slice(2, -2)}</strong>;
          }
          return part;
        })}
        {i < text.split('\n').length - 1 && <br />}
      </span>
    ));
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl overflow-hidden shadow-2xl"
          style={{ maxHeight: '85vh' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header ORANGE Premium */}
          <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] p-4 text-white relative overflow-hidden">
            {/* Decorative circles */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/5 rounded-full" />
            
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Bot size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Assistant IA</h3>
                  <p className="text-sm text-white/90">{shopName}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="relative flex items-center gap-2 mt-3 text-sm text-white/95">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span>En ligne • Réponse immédiate 24/7</span>
            </div>
          </div>
          
          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
            {messages.map((msg, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center mr-2 flex-shrink-0">
                    <Bot size={16} className="text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user' 
                    ? 'bg-[#F97316] text-white rounded-br-md' 
                    : 'bg-white shadow-md border border-gray-100 rounded-bl-md text-gray-700'
                }`}>
                  <p className="text-sm leading-relaxed">{formatMessage(msg.content)}</p>
                </div>
              </motion.div>
            ))}
            {isTyping && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center mr-2 flex-shrink-0">
                  <Bot size={16} className="text-white" />
                </div>
                <div className="bg-white shadow-md border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input */}
          <div className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleSend()}
                placeholder="Pose ta question..."
                className="flex-1 focus-visible:ring-[#F97316] rounded-xl"
              />
              <Button onClick={handleSend} className="bg-[#F97316] hover:bg-[#EA580C] rounded-xl px-4">
                <Send size={18} />
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Version démo • Propulsé par l'IA AbracadaBati
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════
// BOUTON FLOTTANT IA INTELLIGENT - Animation type Messenger (2 cycles)
// ═══════════════════════════════════════════════════════════════
function SmartAIFab({ shopName, onClick }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0); // 0=initial, 1=first, 2=closed, 3=second, 4=done
  
  useEffect(() => {
    // Reset à chaque montage du composant (entrée dans une fiche)
    setAnimationPhase(0);
    setIsExpanded(false);
    
    // Phase 1: Attendre 2 secondes puis ouvrir
    const timer1 = setTimeout(() => {
      setIsExpanded(true);
      setAnimationPhase(1);
    }, 2000);
    
    // Phase 2: Fermer après 3 secondes (total: 5s)
    const timer2 = setTimeout(() => {
      setIsExpanded(false);
      setAnimationPhase(2);
    }, 5000);
    
    // Phase 3: Attendre 5 secondes de plus puis rouvrir (total: 10s)
    const timer3 = setTimeout(() => {
      setIsExpanded(true);
      setAnimationPhase(3);
    }, 10000);
    
    // Phase 4: Fermer définitivement après 3 secondes (total: 13s)
    const timer4 = setTimeout(() => {
      setIsExpanded(false);
      setAnimationPhase(4);
    }, 13000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);
  
  // Extraire le nom court de l'entreprise
  const shortName = shopName?.split(' ').slice(0, 2).join(' ') || 'l\'artisan';
  
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.5, type: 'spring', stiffness: 200, damping: 15 }}
      className="fixed bottom-24 right-4 z-[9000] lg:hidden"
    >
      <motion.button
        onClick={onClick}
        className="flex items-center bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white shadow-2xl rounded-full overflow-hidden"
        style={{
          boxShadow: '0 8px 32px rgba(249, 115, 22, 0.45)'
        }}
        animate={{
          paddingLeft: isExpanded ? 16 : 16,
          paddingRight: isExpanded ? 16 : 16,
          x: isExpanded ? -8 : 0,
        }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        data-testid="fab-ai-chat"
      >
        {/* Icon container - toujours visible */}
        <motion.div 
          className="flex items-center justify-center flex-shrink-0"
          animate={{
            width: 56,
            height: 56,
          }}
        >
          <Bot size={26} />
        </motion.div>
        
        {/* Texte animé - type Messenger */}
        <AnimatePresence mode="wait">
          {isExpanded && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden whitespace-nowrap pr-2"
            >
              <span className="font-semibold text-sm">
                Assistant IA de {shortName}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Pulse effect quand fermé */}
        {!isExpanded && animationPhase < 4 && (
          <>
            <span className="absolute inset-0 rounded-full bg-white/20 animate-ping opacity-30" style={{ animationDuration: '2s' }} />
            <span className="absolute inset-1 rounded-full border-2 border-white/30" />
          </>
        )}
      </motion.button>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ═══════════════════════════════════════════════════════════════
export default function ShopDetailPage({ onBack }) {
  const { shopId: paramShopId, slug: paramSlug } = useParams();
  const location = useLocation();
  // Gère /artisans/:shopId et /boutique/:slug
  const shopId = paramShopId || paramSlug || location.pathname.split('/artisans/')[1] || null;
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeGalleryImage, setActiveGalleryImage] = useState(null);
  // Navigation mobile — section active dans la bottom navbar
  const [activeSection, setActiveSection] = useState('accueil');
  // Helper : visible sur mobile seulement si c'est la section active ; toujours visible sur desktop
  const sec = (name) => activeSection === name ? '' : 'hidden md:block';

  // Stripe Connect — paiement boutique
  const { initiateCheckout, loading: checkoutLoading, error: checkoutError } = useBoutiqueCheckout();
  
  // Swipe detection pour retour arrière (UX mobile)
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);
  
  const handleTouchEnd = useCallback((e) => {
    if (!touchStartX.current || !touchStartY.current) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;
    
    // Swipe vers la droite (retour) - minimum 100px, angle horizontal
    if (deltaX > 100 && Math.abs(deltaY) < 50 && touchStartX.current < 50) {
      navigate('/artisans');
    }
    
    touchStartX.current = null;
    touchStartY.current = null;
  }, [navigate]);
  
  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchEnd]);
  const { requireAuth, isAuthenticated } = useDemoMode();
  
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service_type: ''
  });

  useEffect(() => {
    fetchShop();
  }, [shopId]);

  const fetchShop = async () => {
    setLoading(true);
    let data = null;

    // ── Tentative 0 : Boutique publiée via wizard (nouvelle architecture) ──
    try {
      const res = await fetch(`${API_URL}/api/boutique/${shopId}`);
      if (res.ok) {
        const json = await res.json();
        const b = json.boutique;
        if (b) {
          data = {
            id: b.slug,
            seller_id: b.seller_id,
            slug: b.slug,
            shop_name: b.name,
            metier: b.metier_principal || b.metier,
            metier_name: b.metier_principal || b.metier,
            description: b.description,
            city: b.city,
            postal_code: b.postal_code,
            phone: b.phone,
            email: b.email_pro,
            website: b.website,
            is_verified: b.status === 'active',
            is_demo: false,
            rating: b.average_rating,
            rating_average: b.average_rating,
            reviews_count: b.reviews_count || 0,
            zone_intervention_km: b.zone_intervention_km,
            certifications: b.certifications || [],
            logo: b.logo_url || b.logo || null,
            cover_image: b.cover_photos?.[0] || b.cover_photo || null,
            social_links: b.social_links || {},
            opening_hours: b.opening_hours || [],
            brand_color: b.brand_color,
            services: (json.prestations || []).map(p => ({
              id: p.id,
              name: p.name,
              description_short: p.description_short || p.description,
              description_long: p.description_long,
              tarif_type: p.tarif_type || 'sur_devis',
              tarif_montant: p.tarif_montant,
              tarif_unite: p.tarif_unite || '€',
              photos: p.photos || [],
            })),
            realisations: b.realisations || [],
            posts: b.posts || [],
            reviews: b.reviews || [],
          };
        }
      }
    } catch (e) { /* fallback vers les tentatives suivantes */ }

    // ── Tentative 1 : API artisans cockpit (vrais artisans avec seller_id) ──
    try {
      const res = await fetch(`${API_URL}/api/artisans/${shopId}`);
      if (res.ok) {
        const artisanData = await res.json();
        const rawPrestations = artisanData.prestations_full?.length > 0
          ? artisanData.prestations_full
          : (artisanData.prestations || []);
        const prestationsFull = rawPrestations.map(p => ({
          id: p.id,
          name: p.name,
          description_short: p.description_short || p.description,
          description_long: p.description_long,
          tarif_type: p.tarif_type || p.price_type || 'sur_devis',
          tarif_montant: p.tarif_montant || p.price_from,
          tarif_unite: p.tarif_unite || '€',
          duree_estimee: p.duree_estimee,
          materiaux_fournis: p.materiaux_fournis,
          certifications: p.certifications || [],
          photos: p.photos || []
        }));
        data = {
          id: artisanData.seller_id,
          seller_id: artisanData.seller_id,
          shop_name: artisanData.company_name || artisanData.artisan_name,
          metier: artisanData.metier,
          metier_name: artisanData.metier,
          description: artisanData.description,
          city: artisanData.city,
          postal_code: artisanData.postal_code,
          phone: artisanData.phone,
          email: artisanData.email,
          is_verified: artisanData.is_verified,
          is_demo: artisanData.is_demo,
          rating: artisanData.rating,
          reviews_count: artisanData.reviews_count,
          zone_intervention_km: artisanData.zone_intervention_km,
          certifications: artisanData.certifications || [],
          profile_photo: artisanData.profile_photo,
          services: prestationsFull,
          logo: artisanData.profile_photo,
          cover_image: null
        };
      }
    } catch (e) { /* ignoré, on essaie le suivant */ }

    // ── Tentative 2 : endpoint ecosystem/shops ──
    if (!data) {
      try {
        const res = await fetch(`${API_URL}/api/ecosystem/shops/${shopId}`);
        if (res.ok) data = await res.json();
      } catch (e) { /* ignoré */ }
    }

    // ── Tentative 3 : liste artisans publique ──
    if (!data) {
      try {
        const res = await fetch(`${API_URL}/api/artisans?per_page=200`);
        if (res.ok) {
          const list = await res.json();
          const found = list.artisans?.find(a => a.seller_id === shopId);
          if (found) {
            data = {
              id: found.seller_id,
              seller_id: found.seller_id,
              shop_name: found.company_name || found.artisan_name,
              metier: found.metier || found.metier_name,
              description: found.description,
              city: found.city,
              postal_code: found.postal_code,
              phone: found.phone,
              email: found.email,
              is_verified: found.is_verified,
              rating: found.rating,
              reviews_count: found.reviews_count,
              zone_intervention_km: found.zone_intervention_km,
              certifications: found.certifications || [],
              services: found.prestations || [],
              logo: null,
              cover_image: null
            };
          }
        }
      } catch (e) { /* ignoré */ }
    }

    // ── Tentative 4 : données de démonstration locales (toujours disponibles) ──
    if (!data) {
      const mock = MOCK_ARTISANS.find(a => a.id === shopId || a.slug === shopId);
      if (mock) {
        data = {
          id: mock.id,
          seller_id: mock.id,
          shop_name: mock.shop_name,
          metier: mock.metier,
          description: mock.description,
          city: mock.city,
          postal_code: mock.postal_code,
          phone: mock.phone,
          is_verified: mock.is_verified,
          rating: mock.rating_average,
          rating_average: mock.rating_average,
          reviews_count: mock.reviews_count,
          certifications: [],
          services: (mock.services || []).map((s, i) => ({
            id: `${mock.id}-s${i}`,
            name: s,
            tarif_type: 'sur_devis',
          })),
          logo: mock.logo,
          cover_image: mock.cover_image,
          is_demo: true,
        };
      }
    }

    // ── Enrichissement et affichage ──
    try {
      if (data) {
        
        const DEMO_COVERS = {
          'electricien': 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1200',
          'plombier': 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=1200',
          'macon': 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200',
          'carreleur': 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200',
          'peintre': 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=1200',
          'menuisier': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200',
          'couvreur': 'https://images.pexels.com/photos/31771166/pexels-photo-31771166.jpeg?auto=compress&cs=tinysrgb&w=1200',
          'chauffagiste': 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=1200',
          'default': 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200'
        };
        
        // D'abord calculer le metierSlug (utilisé pour le choix des images)
        const metierSlug = (data.metier || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const coverKey = Object.keys(DEMO_COVERS).find(k => metierSlug.includes(k)) || 'default';
        
        // Images de profil HOMMES uniquement - 20 photos artisans/professionnels (synced avec ArtisansPage)
        const DEMO_LOGOS = [
          'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1599566150163-29194dcabd36?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1639149888905-fb39731f2e6c?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1628157588553-5eeea00af15c?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1463453091185-61582044d556?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1583195764036-6dc248ac07d9?w=400&h=400&fit=crop',
          'https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=400&h=400&fit=crop',
        ];
        
        // Générer un index DÉTERMINISTE basé sur l'ID de l'URL (shopId)
        // Cela garantit la cohérence car c'est le même ID utilisé dans la liste
        const getStableIndex = (id) => {
          if (!id) return 0;
          const str = String(id);
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            hash = hash & hash;
          }
          return Math.abs(hash);
        };
        
        // Utiliser shopId (de l'URL) pour garantir la cohérence avec la liste
        const stableLogoIndex = getStableIndex(shopId) % DEMO_LOGOS.length;
        
        const enrichedData = {
          ...data,
          shop_name: data.name || data.shop_name || data.company_name,
          cover_image: data.cover_image || DEMO_COVERS[coverKey],
          logo: data.logo || DEMO_LOGOS[stableLogoIndex],
          rating_average: data.rating || data.rating_average || 4.7,
          reviews_count: data.reviews_count || Math.floor(Math.random() * 100) + 20,
          response_time: Math.floor(Math.random() * 30) + 5,
          clients_count: Math.floor(Math.random() * 200) + 50,
          experience_years: Math.floor(Math.random() * 15) + 3,
          is_available: true,
          next_slot: 'Aujourd\'hui',
          followers_count: Math.floor(Math.random() * 500) + 100,
          certifications: data.certifications || ['RGE', 'Qualibat'],
          gallery: MOCK_GALLERY,
          reviews: MOCK_REVIEWS,
          posts: data.posts?.length > 0 ? data.posts : [],
          articles: MOCK_ARTICLES,
        };

        setShop(enrichedData);

        // Charger les avis réels de la boutique
        try {
          const avisSlug = data.slug || shopId;
          const avisRes = await fetch(`${API_URL}/api/boutique/${avisSlug}/avis`);
          if (avisRes.ok) {
            const avisData = await avisRes.json();
            if (avisData.reviews?.length > 0) {
              setShop(prev => ({
                ...prev,
                reviews: avisData.reviews,
                rating_average: avisData.average_rating || prev.rating_average,
                reviews_count: avisData.total || prev.reviews_count,
              }));
            }
          }
        } catch (e) { /* garde MOCK_REVIEWS en fallback */ }

        // Charger le fil de publications réelles de la boutique
        try {
          const feedSlug = data.slug || shopId;
          const feedRes = await fetch(`${API_URL}/api/boutique/${feedSlug}/feed`);
          if (feedRes.ok) {
            const feedData = await feedRes.json();
            if (feedData.posts?.length > 0) {
              setShop(prev => ({ ...prev, posts: feedData.posts }));
            }
          }
        } catch (e) { /* garde les posts existants */ }
      }
    } catch (e) {
      console.error('Error fetching shop:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      requireAuth(e, 'demander un devis');
      return;
    }
    
    setSubmitting(true);

    try {
      const token = localStorage.getItem('client_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const payload = {
        prestation_name: quoteForm.service_type || 'Demande de devis',
        artisan_id: shop.seller_id || shop.id,
        artisan_shop_name: shop.shop_name,
        description: quoteForm.message,
        phone: quoteForm.phone,
        client_name: quoteForm.name,
        client_email: quoteForm.email,
        city: shop.city || '',
        postal_code: shop.postal_code || '',
      };

      // Authenticated direct request if token, public request otherwise
      const endpoint = token
        ? `${API_URL}/api/artisans/devis-requests`
        : `${API_URL}/api/artisans/devis-requests/public`;

      if (!token) {
        // public endpoint requires client_name and client_email
        payload.project_type = quoteForm.service_type || '';
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success('Demande de devis envoyée !', {
          description: `${shop.shop_name} te contactera rapidement.`
        });
        setShowQuoteForm(false);
        setQuoteForm({ name: '', email: '', phone: '', message: '', service_type: '' });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de l\'envoi');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleFollow = () => {
    if (!isAuthenticated) {
      requireAuth(null, 'suivre cet artisan');
      return;
    }
    setIsFollowing(!isFollowing);
    toast.success(isFollowing ? 'Tu ne suis plus cet artisan' : 'Tu suis maintenant cet artisan !');
  };
  
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: shop.shop_name,
        text: `Découvre ${shop.shop_name}, ${shop.metier} sur AbracadaBati`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié !');
    }
  };
  
  const handleMessage = async () => {
    if (!isAuthenticated) {
      requireAuth(null, 'envoyer un message à cet artisan');
      return;
    }
    const artisanId = shop?.seller_id || shop?.id;
    if (!artisanId) return;
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('social_token') || localStorage.getItem('client_token') || '';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ type: 'direct', participant_ids: [artisanId], category: 'artisan' })
      });
      if (res.ok) {
        const conv = await res.json();
        window.location.href = `/messages/${conv.id}`;
      } else {
        toast.error('Impossible d\'ouvrir la messagerie');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F97316]"></div>
          <p className="text-gray-500">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Building2 size={60} className="text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Artisan non trouvé</h1>
        {onBack ? (
          <Button variant="outline" onClick={onBack}>Retour aux artisans</Button>
        ) : (
          <Link to="/artisans">
            <Button variant="outline">Retour aux artisans</Button>
          </Link>
        )}
      </div>
    );
  }

  const isOnAbracada = typeof window !== 'undefined' && window.location.hostname.includes('abracada');

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20 md:pb-0" data-testid="shop-detail-page">

      {/* ═══════════════════════════════════════════════════════════════
          COVER IMAGE + NAVIGATION + PHOTO PREMIUM OVERLAY
      ═══════════════════════════════════════════════════════════════ */}
      <div className="h-52 md:h-80 lg:h-96 relative">
        {shop.cover_image && (
          <img src={shop.cover_image} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Navigation Premium */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          {onBack ? (
            <button
              onClick={onBack}
              className="group flex items-center gap-2.5 px-4 py-2.5 bg-black/30 backdrop-blur-xl text-white rounded-full shadow-lg border border-white/10 hover:bg-black/40 hover:border-white/20 transition-all duration-200 active:scale-95"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
            >
              <ArrowLeft size={18} className="group-hover:-translate-x-0.5 transition-transform" />
              <span className="font-medium text-sm">Retour aux artisans</span>
            </button>
          ) : (
            <Link
              to="/artisans"
              className="group flex items-center gap-2.5 px-4 py-2.5 bg-black/30 backdrop-blur-xl text-white rounded-full shadow-lg border border-white/10 hover:bg-black/40 hover:border-white/20 transition-all duration-200 active:scale-95"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
            >
              <ArrowLeft size={18} className="group-hover:-translate-x-0.5 transition-transform" />
              <span className="font-medium text-sm">Retour aux artisans</span>
            </Link>
          )}
          
          <button
            onClick={handleShare}
            className="flex items-center justify-center w-10 h-10 bg-black/30 backdrop-blur-xl text-white rounded-full shadow-lg border border-white/10 hover:bg-black/40 hover:border-white/20 transition-all duration-200 active:scale-95"
            style={{
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}
          >
            <Share2 size={18} />
          </button>
        </div>

        {/* Pills navigation flottants — desktop uniquement, bas de cover */}
        {(() => {
          const brandColor = shop?.brand_color || '#F97316';
          const tabs = [
            { id: 'services-section', label: 'Services', icon: Wrench },
            { id: 'galerie-section', label: 'Galerie', icon: Camera },
            { id: 'avis-section', label: 'Avis', icon: Star },
            { id: 'actu-section', label: 'Actu', icon: Newspaper },
            { id: 'videos-section', label: 'Vidéos', icon: Film },
          ];
          return (
            <div className="absolute bottom-5 left-0 right-0 hidden md:flex justify-center gap-2.5 px-6 z-10">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => document.getElementById(tab.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="flex items-center gap-1.5 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full text-sm font-semibold text-gray-800 shadow-lg border border-white/60 hover:bg-white hover:scale-105 transition-all duration-200 active:scale-95"
                  style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.8)' }}
                  onMouseEnter={e => { e.currentTarget.style.color = brandColor; }}
                  onMouseLeave={e => { e.currentTarget.style.color = ''; }}
                >
                  <tab.icon size={13} />
                  {tab.label}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Photo de profil Premium - Overlay sur la cover (MOBILE ONLY) */}
        <button
          className="absolute -bottom-14 left-4 z-10 md:hidden"
          onClick={() => setActiveSection('accueil')}
          aria-label="Accueil boutique"
        >
          <div className={`w-28 h-28 rounded-2xl bg-white shadow-2xl border-4 overflow-hidden transition-all ${activeSection === 'accueil' ? 'border-[#F97316]' : 'border-white'}`}>
            {shop.logo ? (
              <img src={shop.logo} alt={shop.shop_name} className="w-full h-full object-cover object-top" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <Building2 size={48} className="text-gray-400" />
              </div>
            )}
          </div>
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MAIN CONTENT
      ═══════════════════════════════════════════════════════════════ */}
      <div className="max-w-6xl mx-auto px-4 pt-2 md:pt-24 relative pb-8">
        
        {/* ═══ HEADER INFO - Layout adaptatif Mobile/Desktop ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl mb-4"
          style={{ overflow: 'visible' }}
        >
          {/* Mobile: Layout vertical (texte sous la photo) */}
          <div className="block md:hidden p-4 pt-16">
            {/* Nom + Badge Vérifié */}
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900">{shop.shop_name}</h1>
              {shop.is_verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                  <Shield size={10} /> Vérifié
                </span>
              )}
            </div>
            
            {/* Métier */}
            <p className="text-[#F97316] font-semibold text-base mb-2">{shop.metier}</p>
            
            {/* Ville + Note sur une ligne */}
            <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
              <div className="flex items-center gap-1">
                <MapPin size={12} className="text-gray-400" />
                <span>{shop.city}</span>
              </div>
              <span className="text-gray-300">•</span>
              <div className="flex items-center gap-1">
                <Star size={12} className="text-amber-400 fill-amber-400" />
                <span className="font-semibold">{shop.rating_average?.toFixed(1)}</span>
                <span className="text-gray-400">({shop.reviews_count})</span>
              </div>
            </div>
            
            {/* Badges sur UNE ligne */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {shop.is_demo && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full text-xs font-medium text-amber-700">
                  <Sparkles size={10} />
                  Démo
                </span>
              )}
              {shop.rating_average >= 4.5 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-50 border border-yellow-200 rounded-full text-xs font-medium text-yellow-700">
                  <Award size={10} />
                  Top
                </span>
              )}
              {shop.clients_count > 100 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700">
                  <TrendingUp size={10} />
                  Demandé
                </span>
              )}
            </div>
          </div>
          
          {/* Mobile: Disponibilité compacte */}
          <div className="md:hidden px-4 pb-3 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="font-medium text-emerald-700">Disponible</span>
              </div>
              <span className="text-gray-300">•</span>
              <div className="flex items-center gap-1">
                <Zap size={12} className="text-blue-600" />
                <span className="font-medium text-blue-700">~{shop.response_time} min</span>
              </div>
            </div>
          </div>

          {/* Desktop/Tablet: Layout 3 colonnes (Image | Infos | Assistant IA) */}
          <div className="hidden md:block p-6" style={{ overflow: 'visible' }}>
            <div className="flex items-start gap-6" style={{ overflow: 'visible' }}>
              {/* COLONNE 1 - Photo de profil ENTIÈRE */}
              <div className="flex-shrink-0" style={{ marginTop: '-100px', overflow: 'visible' }}>
                <div 
                  style={{ 
                    width: '140px',
                    backgroundColor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    border: '4px solid white',
                    overflow: 'visible',
                    padding: '0'
                  }}
                >
                  {shop.logo ? (
                    <img 
                      src={shop.logo} 
                      alt={shop.shop_name} 
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        borderRadius: '12px'
                      }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '140px', background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' }}>
                      <Building2 size={40} className="text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* COLONNE 2 - Informations artisan */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{shop.shop_name}</h1>
                  {shop.is_verified && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                      <Shield size={12} /> Vérifié
                    </span>
                  )}
                  <button
                    onClick={handleFollow}
                    className={`ml-2 px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-all text-xs font-medium ${
                      isFollowing 
                        ? 'bg-[#F97316] text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Heart size={14} fill={isFollowing ? 'currentColor' : 'none'} />
                    <span>{isFollowing ? 'Suivi' : 'Suivre'}</span>
                  </button>
                </div>
                
                <p className="text-[#F97316] font-semibold text-lg mb-2">{shop.metier}</p>
                
                <div className="flex items-center gap-3 text-gray-600 flex-wrap text-sm mb-3">
                  <div className="flex items-center gap-1">
                    <MapPin size={14} className="text-gray-400" />
                    <span>{shop.city} ({shop.postal_code})</span>
                  </div>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-1">
                    <Star size={14} className="text-amber-400 fill-amber-400" />
                    <span className="font-semibold">{shop.rating_average?.toFixed(1)}</span>
                    <span className="text-gray-400">({shop.reviews_count} avis)</span>
                  </div>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-1">
                    <Users size={14} className="text-gray-400" />
                    <span>{shop.clients_count}+ clients</span>
                  </div>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-1">
                    <Calendar size={14} className="text-gray-400" />
                    <span>{shop.experience_years} ans</span>
                  </div>
                </div>
                
                {/* Badges + Disponibilité sur une ligne */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-xs font-medium text-emerald-700">Disponible {shop.next_slot}</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full">
                    <Zap size={12} className="text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">~{shop.response_time} min</span>
                  </div>
                  {shop.is_demo && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-xs font-medium text-amber-700">
                      <Sparkles size={10} />
                      Démo
                    </span>
                  )}
                  {shop.rating_average >= 4.5 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-yellow-50 border border-yellow-200 rounded-full text-xs font-medium text-yellow-700">
                      <Award size={10} />
                      Top
                    </span>
                  )}
                  {shop.clients_count > 100 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700">
                      <TrendingUp size={10} />
                      Demandé
                    </span>
                  )}
                </div>
                
                {/* Zone d'intervention + Garanties (Crédibilité métier) */}
                <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <MapPin size={14} className="text-[#F97316]" />
                    <span>Zone : <strong className="text-gray-800">{shop.city} + 30 km</strong></span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full text-xs font-semibold text-green-700">
                    <Shield size={12} />
                    Garantie décennale
                  </span>
                </div>
                
                {/* À propos - Texte court (2 lignes max) */}
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-3">
                  {shop.description || `${shop.shop_name} est un professionnel qualifié en ${shop.metier?.toLowerCase()}, intervenant dans la région ${shop.city}.`}
                </p>
                
                {/* Services proposés - Intégrés dans le bloc principal */}
                {shop.services?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(shop.services) ? shop.services.slice(0, 4) : []).map((service, idx) => {
                      const serviceName = typeof service === 'string' ? service : service.name;
                      return (
                        <span
                          key={idx}
                          className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-xs font-medium text-gray-700 cursor-pointer transition-colors"
                          onClick={() => {
                            setQuoteForm({ ...quoteForm, service_type: serviceName });
                            setShowQuoteForm(true);
                          }}
                        >
                          {serviceName}
                        </span>
                      );
                    })}
                    {shop.services.length > 4 && (
                      <span className="inline-flex items-center px-3 py-1.5 bg-gray-50 rounded-full text-xs text-gray-500">
                        +{shop.services.length - 4}
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {/* COLONNE 3 - Zone Action (alignée avec le nom) */}
              <div className="hidden md:flex flex-col gap-3 flex-shrink-0 w-56 lg:w-72">
                {/* Assistant IA */}
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center shadow">
                      <Bot size={18} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">Assistant IA</h3>
                      <p className="text-xs text-gray-500">de {shop.shop_name}</p>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                    Pose tes questions, obtiens des réponses instantanées.
                  </p>
                  
                  <Button
                    onClick={() => setShowAIChat(true)}
                    className="w-full bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#DC2626] text-white font-semibold h-9 text-sm rounded-lg shadow-md"
                  >
                    <MessageCircle size={14} className="mr-2" />
                    Discuter avec l'assistant
                  </Button>
                </div>
                
                {/* CTAs */}
                <div className="bg-white rounded-xl p-4 shadow-sm space-y-2">
                  {shop.phone && (
                    <a
                      href={`tel:${shop.phone}`}
                      className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm text-white transition-opacity hover:opacity-90 bg-[#F97316]"
                    >
                      <Phone size={15} /> Appeler maintenant
                    </a>
                  )}
                  <Button
                    onClick={() => setShowQuoteForm(true)}
                    className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white font-semibold h-10 text-sm"
                    data-testid="cta-devis-header"
                  >
                    <FileText size={16} className="mr-2" />
                    Demander un devis
                  </Button>

                  <Button
                    onClick={handleMessage}
                    variant="outline"
                    className="w-full border-gray-200 text-gray-700 hover:bg-gray-50 font-medium h-9 text-sm"
                  >
                    <MessageSquare size={14} className="mr-2" />
                    Envoyer un message
                  </Button>

                </div>
              </div>
            </div>
          </div>
        </motion.div>


        {/* ═══════════════════════════════════════════════════════════════
            SECTION ACCUEIL MOBILE — visible uniquement sur mobile en section 'accueil'
        ═══════════════════════════════════════════════════════════════ */}
        <div className={`md:hidden mb-4 space-y-3 ${activeSection === 'accueil' ? 'block' : 'hidden'}`}>
          {/* Description */}
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <p className="text-gray-700 text-sm leading-relaxed">
              {shop.description || 'Artisan professionnel à votre service.'}
            </p>
            {shop.zone_intervention_km && (
              <div className="flex items-center gap-1.5 mt-3 text-sm text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg w-fit">
                <MapPin size={13} />
                <span>Intervient à {shop.city} + {shop.zone_intervention_km} km</span>
              </div>
            )}
          </div>

          {/* CTAs principaux */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-2">
            {shop.phone && (
              <a href={`tel:${shop.phone}`} className="flex items-center justify-center gap-2 w-full py-3 bg-[#1E3A5F] text-white rounded-xl font-semibold text-sm">
                <Phone size={16} /> Appeler
              </a>
            )}
            <Button onClick={() => setShowQuoteForm(true)} className="w-full bg-[#F97316] hover:bg-[#EA580C] font-semibold h-10 text-sm">
              <FileText size={15} className="mr-2" /> Demander un devis
            </Button>
            <Button onClick={handleMessage} variant="outline" className="w-full border-gray-200 h-9 text-sm">
              <MessageSquare size={14} className="mr-2" /> Envoyer un message
            </Button>
            <Button onClick={() => setShowAIChat(true)} variant="outline" className="w-full border-[#F97316] text-[#F97316] h-9 text-sm">
              <Bot size={14} className="mr-2" /> Discuter avec l'assistant IA
            </Button>
          </div>

          {/* Boutons Galerie + Blog */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setActiveSection('services')}
              className="bg-white rounded-xl p-4 shadow-sm flex flex-col items-center gap-2 hover:shadow-md transition-shadow"
            >
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <Camera size={20} className="text-purple-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">Galerie</span>
            </button>
            <button
              onClick={() => setActiveSection('actu')}
              className="bg-white rounded-xl p-4 shadow-sm flex flex-col items-center gap-2 hover:shadow-md transition-shadow"
            >
              <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                <BookOpen size={20} className="text-indigo-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">Blog & Actu</span>
            </button>
          </div>

          {/* Coordonnées */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-2 text-sm">
            {shop.email && (
              <a href={`mailto:${shop.email}`} className="flex items-center gap-3 text-gray-600">
                <Mail size={15} className="text-gray-400" /> {shop.email}
              </a>
            )}
            {shop.city && (
              <div className="flex items-center gap-3 text-gray-600">
                <MapPin size={15} className="text-gray-400" /> {shop.city} {shop.postal_code}
              </div>
            )}
          </div>

          {/* Certifications */}
          {shop.certifications?.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex flex-wrap gap-2">
                {shop.certifications.map((cert, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                    <CheckCircle size={11} /> {cert}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] lg:grid-cols-3 gap-6">
          {/* ═══ COLONNE PRINCIPALE (2/3) ═══ */}
          <div className="md:col-span-1 lg:col-span-2 space-y-6">

            {/* Services proposés avec prix - DONNÉES COCKPIT */}
            {shop.services?.length > 0 && (
              <motion.div
                id="services-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`bg-white rounded-xl p-5 shadow-sm ${sec('services')}`}
              >
                <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Wrench size={18} style={{ color: shop?.brand_color || '#F97316' }} />
                  Services proposés
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(Array.isArray(shop.services) ? shop.services : []).map((service, idx) => {
                    // Données réelles du cockpit pro
                    const serviceData = typeof service === 'string' 
                      ? { name: service, tarif_type: 'sur_devis' }
                      : service;
                    
                    // Formatage du tarif selon le type
                    const formatTarif = () => {
                      if (serviceData.tarif_type === 'forfait' && serviceData.tarif_montant) {
                        return `À partir de ${serviceData.tarif_montant}${serviceData.tarif_unite || '€'}`;
                      } else if (serviceData.tarif_type === 'horaire' && serviceData.tarif_montant) {
                        return `${serviceData.tarif_montant}€/h`;
                      } else if (serviceData.tarif_type === 'gratuit') {
                        return 'Gratuit';
                      }
                      return 'Sur devis';
                    };
                    
                    const tarifColor = serviceData.tarif_type === 'gratuit' ? 'text-emerald-600' :
                                      serviceData.tarif_type === 'sur_devis' ? 'text-purple-600' : 'text-[#F97316]';
                    
                    const hasFixedPrice = serviceData.tarif_type === 'forfait' && serviceData.tarif_montant > 0;
                    const sellerId = shop?.seller_id || shop?.id;

                    return (
                      <div
                        key={serviceData.id || idx}
                        className="p-4 bg-gray-50 rounded-xl hover:bg-white hover:shadow-md hover:border-gray-200 border border-transparent transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => {
                              setQuoteForm({ ...quoteForm, service_type: serviceData.name });
                              setShowQuoteForm(true);
                            }}
                          >
                            <h3 className="font-semibold text-gray-900 text-sm mb-1">{serviceData.name}</h3>
                            {serviceData.description_short && (
                              <p className="text-gray-600 text-xs line-clamp-2">{serviceData.description_short}</p>
                            )}
                            {serviceData.duree_estimee && (
                              <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                                <Clock size={12} />
                                <span>{serviceData.duree_estimee}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2 ml-4">
                            <span className={`font-semibold text-sm ${tarifColor}`}>{formatTarif()}</span>
                            {hasFixedPrice && sellerId ? (
                              <button
                                onClick={() => initiateCheckout({
                                  sellerId,
                                  productName: serviceData.name,
                                  amount: serviceData.tarif_montant,
                                  productId: serviceData.id,
                                })}
                                disabled={checkoutLoading}
                                className="flex items-center gap-1 bg-[#F97316] text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-orange-600 transition disabled:opacity-60"
                              >
                                {checkoutLoading ? <Loader2 size={12} className="animate-spin" /> : <Euro size={12} />}
                                Payer
                              </button>
                            ) : (
                              <ChevronRight
                                size={14}
                                className="text-gray-300 group-hover:text-[#F97316] transition-colors cursor-pointer"
                                onClick={() => {
                                  setQuoteForm({ ...quoteForm, service_type: serviceData.name });
                                  setShowQuoteForm(true);
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Réalisations - Photos de travaux */}
            {shop.services?.length > 0 && (
              <motion.div
                id="galerie-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className={`bg-white rounded-xl p-5 shadow-sm ${sec('services')}`}
              >
                <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Camera size={18} style={{ color: shop?.brand_color || '#F97316' }} />
                  Réalisations
                </h2>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {/* 4 images différentes selon le métier */}
                  {(shop.metier?.toLowerCase().includes('menuisier') ? [
                    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1588854337236-6889d631faa8?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1565791380713-1756b9a05343?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=300&h=300&fit=crop'
                  ] : shop.metier?.toLowerCase().includes('electr') ? [
                    'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1558618047-f4b511d2e0e6?w=300&h=300&fit=crop'
                  ] : [
                    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1541123603104-512919d6a96c?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=300&h=300&fit=crop',
                    'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=300&h=300&fit=crop'
                  ]).map((imgUrl, idx) => (
                    <div
                      key={idx}
                      className="aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-pointer group"
                    >
                      <img
                        src={imgUrl}
                        alt={`Réalisation ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Avis */}
            <motion.div
              id="avis-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`bg-white rounded-xl p-5 shadow-sm ${sec('avis')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Star size={18} className="text-yellow-500" />
                  Avis clients
                </h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1.5 rounded-full">
                    <Star size={16} className="text-yellow-500 fill-yellow-500" />
                    <span className="text-lg font-bold text-gray-900">{shop.rating_average?.toFixed(1)}</span>
                  </div>
                  <span className="text-sm text-gray-500">({shop.reviews_count})</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <ThumbsUp size={14} className="text-green-500" />
                  <span className="text-sm text-gray-700">98% recommandent</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-500" />
                  <span className="text-sm text-gray-700">{shop.clients_count} clients satisfaits</span>
                </div>
              </div>
              
              <div className="space-y-3">
                {shop.reviews?.slice(0, 3).map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <img src={review.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-medium text-gray-900 text-sm">{review.author}</span>
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={10} className={i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} />
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm">{review.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Actualités */}
            <motion.div
              id="actu-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className={`bg-white rounded-xl p-5 shadow-sm ${sec('actu')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <MessageCircle size={18} className="text-green-600" />
                  Actualités
                </h2>
                <button
                  onClick={handleFollow}
                  className={`px-4 py-2 rounded-full flex items-center gap-2 transition-all text-sm font-medium ${
                    isFollowing 
                      ? 'bg-[#F97316] text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Heart size={14} fill={isFollowing ? 'currentColor' : 'none'} />
                  <span>{isFollowing ? 'Suivi' : 'Suivre'}</span>
                </button>
              </div>
              
              <div className="space-y-3">
                {shop.posts?.map((post) => (
                  <div key={post.id} className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-gray-700 text-sm mb-2">{post.content}</p>
                    {post.image && (
                      <img src={post.image} alt="" className="w-full h-32 object-cover rounded-lg mb-2" />
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><ThumbsUp size={12} /> {post.likes}</span>
                        <span className="flex items-center gap-1"><MessageCircle size={12} /> {post.comments}</span>
                      </div>
                      <span>{new Date(post.date).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Section Videos */}
            <div id="videos-section" className={`bg-white rounded-xl p-8 shadow-sm text-center ${sec('videos')}`}>
              <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Film size={32} className="text-rose-500" />
              </div>
              <h2 className="font-bold text-gray-900 mb-2">Vidéos & Reels</h2>
              <p className="text-sm text-gray-500">
                L'espace vidéo de {shop.shop_name} arrive bientôt.<br />
                Shorts, avant/après chantier, conseils...
              </p>
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-rose-50 text-rose-600 rounded-full text-xs font-medium">
                <Sparkles size={12} /> Disponible prochainement
              </div>
            </div>

            {/* Articles */}
            {shop.articles?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`bg-white rounded-xl p-5 shadow-sm ${sec('actu')}`}
              >
                <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpen size={18} className="text-indigo-600" />
                  Conseils & Articles
                </h2>
                
                <div className="space-y-2">
                  {shop.articles.map((article) => (
                    <div key={article.id} className="p-3 border border-gray-100 rounded-xl hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer">
                      <h3 className="font-medium text-gray-900 text-sm mb-1">{article.title}</h3>
                      <p className="text-xs text-gray-500">{article.excerpt}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
                        <span>{article.readTime}</span>
                        <span>•</span>
                        <span>{new Date(article.date).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* ═══ SIDEBAR (1/3) - Desktop uniquement ═══ */}
          <div className="hidden md:block space-y-4">


            {/* Infos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-5 shadow-sm"
            >
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 size={18} className="text-gray-600" />
                Informations
              </h2>
              
              <div className="space-y-4">
                {/* Zone d'intervention - Améliorée SEO */}
                <div>
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                    <MapPin size={12} />
                    Zone d'intervention
                  </p>
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <p className="text-blue-800 font-medium text-sm">
                      {shop.city} + 25 km
                    </p>
                    {shop.zone_intervention && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(Array.isArray(shop.zone_intervention) ? shop.zone_intervention : [shop.zone_intervention]).map((zone, idx) => (
                          <span key={idx} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                            {zone}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Certifications */}
                {shop.certifications?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                      <Shield size={12} />
                      Certifications & Labels
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {shop.certifications.map((cert, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                          <CheckCircle size={12} />
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Stats */}
                <div className="pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900">{shop.experience_years}</p>
                    <p className="text-xs text-gray-500">ans d'expérience</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900">{shop.clients_count}+</p>
                    <p className="text-xs text-gray-500">clients satisfaits</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-white rounded-xl p-5 shadow-sm"
            >
              <h2 className="text-base font-semibold text-gray-900 mb-3">Contact</h2>
              <div className="space-y-2">
                {shop.email && (
                  <a href={`mailto:${shop.email}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors text-sm">
                    <Mail size={16} className="text-gray-500" />
                    <span className="text-gray-700 truncate">{shop.email}</span>
                  </a>
                )}
                {shop.city && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-sm">
                    <MapPin size={16} className="text-gray-500" />
                    <span className="text-gray-700">{shop.city}, {shop.postal_code}</span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* CGV - Conditions Générales de Vente */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-5 shadow-sm"
            >
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileCheck size={18} className="text-gray-600" />
                Documents légaux
              </h2>
              <div className="space-y-2">
                <button 
                  onClick={() => toast.info('Document disponible prochainement', { description: 'Les CGV seront téléchargeables dès l\'ouverture officielle.' })}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors text-sm group"
                >
                  <div className="flex items-center gap-3">
                    <FileText size={16} className="text-gray-500" />
                    <span className="text-gray-700">Conditions générales de vente</span>
                  </div>
                  <Download size={16} className="text-gray-400 group-hover:text-[#F97316] transition-colors" />
                </button>
                <button 
                  onClick={() => toast.info('Document disponible prochainement', { description: 'Les mentions légales seront téléchargeables dès l\'ouverture officielle.' })}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors text-sm group"
                >
                  <div className="flex items-center gap-3">
                    <Shield size={16} className="text-gray-500" />
                    <span className="text-gray-700">Mentions légales</span>
                  </div>
                  <Download size={16} className="text-gray-400 group-hover:text-[#F97316] transition-colors" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ═══ BOUTON FLOTTANT IA — désactivé, géré via le bouton Accueil ═══ */}
      {/* <SmartAIFab shopName={shop.shop_name} onClick={() => setShowAIChat(true)} /> */}

      {/* ═══ MODALS ═══ */}
      <ArtisanAIChat shop={shop} isOpen={showAIChat} onClose={() => setShowAIChat(false)} />

      {showQuoteForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Demande de devis</h2>
              <button onClick={() => setShowQuoteForm(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <X size={20} />
              </button>
            </div>

            <div className="flex items-center gap-3 mb-6 p-3 bg-orange-50 rounded-xl">
              {shop.logo && <img src={shop.logo} alt="" className="w-12 h-12 rounded-xl object-cover" />}
              <div>
                <p className="font-semibold text-gray-900">{shop.shop_name}</p>
                <p className="text-sm text-[#F97316]">{shop.metier}</p>
              </div>
            </div>

            <form onSubmit={handleQuoteSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ton nom *</label>
                <Input required value={quoteForm.name} onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })} placeholder="Jean Dupont" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <Input type="email" required value={quoteForm.email} onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })} placeholder="jean@exemple.fr" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                <Input type="tel" required value={quoteForm.phone} onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })} placeholder="06 12 34 56 78" />
              </div>
              {quoteForm.service_type && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service demandé</label>
                  <Input value={quoteForm.service_type} readOnly className="bg-orange-50" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Décris ton projet *</label>
                <Textarea required value={quoteForm.message} onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })} placeholder="Décris ton besoin..." rows={4} />
              </div>
              <Button type="submit" className="w-full bg-[#F97316] hover:bg-[#EA580C]" disabled={submitting}>
                {submitting ? <Loader2 size={18} className="animate-spin mr-2" /> : <Send size={18} className="mr-2" />}
                {submitting ? 'Envoi...' : 'Envoyer ma demande'}
              </Button>
            </form>
          </motion.div>
        </div>
      )}

      {activeGalleryImage && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setActiveGalleryImage(null)}>
          <button onClick={() => setActiveGalleryImage(null)} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full">
            <X size={24} />
          </button>
          <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            {activeGalleryImage.type === 'before_after' ? (
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-white text-center mb-2 font-medium">Avant</p><img src={activeGalleryImage.before} alt="Avant" className="w-full rounded-lg" /></div>
                <div><p className="text-white text-center mb-2 font-medium">Après</p><img src={activeGalleryImage.after} alt="Après" className="w-full rounded-lg" /></div>
              </div>
            ) : (
              <img src={activeGalleryImage.image} alt="" className="w-full rounded-lg" />
            )}
            <p className="text-white text-center mt-4 font-medium">{activeGalleryImage.title}</p>
          </div>
        </div>
      )}
      
      
      {/* ═══════════════════════════════════════════════════════════════
          BOTTOM NAVBAR MOBILE — couleur active = brand_color du vendeur
      ═══════════════════════════════════════════════════════════════ */}
      {(() => {
        // Couleur active = brand_color du vendeur (fallback orange AbracadaWorld)
        const brandColor = shop?.brand_color || '#F97316';
        return (
      <nav
        className="fixed bottom-0 left-0 right-0 md:hidden z-40 bg-white border-t border-gray-100"
        style={{ boxShadow: '0 -4px 20px rgba(0,0,0,0.08)', borderTopColor: brandColor + '33' }}
      >
        <div className="flex items-center justify-around h-16 px-1">
          {[
            { id: 'actu',     icon: Newspaper, label: 'Actu'     },
            { id: 'videos',   icon: Film,      label: 'Vidéos'   },
            { id: 'avis',     icon: Star,      label: 'Avis'     },
            { id: 'services', icon: Wrench,    label: shop?.services?.length > 0 ? 'Services' : 'Produits' },
          ].map(item => {
            const isActive = activeSection === item.id;
            return (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className="flex flex-col items-center gap-0.5 px-3 py-1 min-w-0"
            >
              <item.icon
                size={22}
                style={{ color: isActive ? brandColor : undefined }}
                className={isActive ? '' : 'text-gray-400'}
                fill={isActive && item.id === 'avis' ? brandColor : 'none'}
              />
              <span
                className={`text-[10px] font-medium ${isActive ? '' : 'text-gray-400'}`}
                style={isActive ? { color: brandColor } : {}}
              >
                {item.label}
              </span>
            </button>
          )})}
          <Link
            to="/chez-moi"
            className="flex flex-col items-center gap-0.5 px-3 py-1 min-w-0"
          >
            <Home size={22} className="text-gray-400" />
            <span className="text-[10px] font-medium text-gray-400">
              {isOnAbracada ? 'Chez moi' : 'Mon espace'}
            </span>
          </Link>
        </div>
      </nav>
        );
      })()}
    </div>
  );
}
